import Vue from "vue";
import Router from "vue-router";
import store from "@/store/index";
import {
  getLessonsById,
  getModuleById,
  getStudentById,
  getLessonProgressById,
  getLessonProgress,
  postLessonProgress,
  getModuleProgress,
  postModuleProgress,
} from "@/api/api";

function loadComponent(view) {
  return () => import(/* webpackChunkName: "view-[request]" */ `@/components/${view}.vue`);
}

Vue.use(Router);

export default new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/auth",
      component: loadComponent("Auth"),
      children: [
        {
          path: "login",
          name: "login",
          component: loadComponent("Login"),
          meta: {
            editorCan: true,
            assistantCan: true,
          },
        },
        {
          path: "forgot",
          name: "forgot",
          component: loadComponent("Forgot"),
          meta: {
            editorCan: true,
            assistantCan: true,
          },
        },
        {
          path: "recover/:uid/:token",
          name: "recover",
          component: loadComponent("Recover"),
          meta: {
            editorCan: true,
            assistantCan: true,
          },
        },
      ],
    },
    {
      path: "/check/:student__uuid/:lesson__uuid",
      name: "check",
      meta: {
        requiresAuth: true,
      },
      async beforeEnter(to, from, next) {
        if (store.getters.profile?.role !== "TEACHER") {
          next({ name: "modules" });
          return;
        }
        try {
          let student = await getStudentById(to.params.student__uuid);
          store.commit("updateStudent", student.data);

          const lesson = await getLessonsById(to.params.lesson__uuid);
          store.commit("modules/currentLesson", lesson.data);

          const modul = await getModuleById(lesson.data.module);
          store.commit("modules/selectedModule", modul.data);

          const modulesProgress = await getModuleProgress({
            module__uuid: lesson.data.module,
            student__email: student.data.email,
          });
          let moduleProgress = modulesProgress.data.results[0];
          if (!moduleProgress) {
            const created = await postModuleProgress({
              module__uuid: lesson.data.module,
              student__email: student.data.email,
            });
            moduleProgress = created.data;
          }
          store.commit("progress/module", moduleProgress);

          const progresses = await getLessonProgress({
            student__email: student.data.email,
            lesson__uuid: to.params.lesson__uuid,
          });
          let progress = progresses.data.results[0];
          if (!progress) {
            const created = await postLessonProgress({
              lesson: to.params.lesson__uuid,
              module_progress: moduleProgress.uuid,
              student: student.data.email,
              completed: false,
            });
            progress = created.data;
          }
          const progressDetail = await getLessonProgressById(progress.uuid);
          store.commit("progress/lesson", progressDetail.data);

          await store.dispatch("lesson/teacher_lesson", lesson.data.teacher_lesson);
          await store.dispatch("lesson/practice", lesson.data.practice);
          await store.dispatch("lesson/grammar", lesson.data.grammar);

          let name = "lesson-view";
          if (to.query.section) {
            let section = to.query.section;
            if (section === "practice") {
              name = "practice";
            } else if (section === "grammar") {
              name = "grammar";
            }
          }
          next({ name: name });
        } catch {
          next({ name: "modules" });
        }
      },
    },
    {
      path: "/cloud",
      name: "cloud",
      component: loadComponent("Cloud"),
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: ":level__uuid",
          name: "cloud-level",
          component: loadComponent("CloudLevel"),
          children: [
            {
              path: ":path",
              name: "cloud-folder",
              component: loadComponent("CloudFolder"),
            },
          ],
        },
      ],
    },
    {
      path: "/site",
      name: "site-main",
      redirect: {
        name: "modules",
      },
      component: loadComponent("Sites"),
      beforeEnter: (to, from, next) => {
        const role = store.getters.profile?.role;
        if (role && ["ADMIN", "EDITOR"].includes(role)) {
          next({ name: "admin" });
        } else if (role && ["ASSISTANT"].includes(role)) {
          next({ name: "admin-active-contacts" });
        } else next();
      },
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: "lessons",
          name: "lessons",
          component: loadComponent("Lessons/Main"),
          beforeEnter: (to, from, next) => {
            if (!store.getters["modules/selectedModule"]) {
              next({ name: "modules" });
            } else next();
          },
        },
        {
          path: "modules",
          name: "modules",
          component: loadComponent("Lessons/Modules"),
        },
        {
          path: "portal-vuex-handoff",
          name: "portal-vuex-handoff",
          component: loadComponent("Lessons/PortalVuexHandoff"),
        },
        {
          path: "lessons-view",
          name: "lesson-view",
          component: loadComponent("Lessons/Lessons"),
          beforeEnter: (to, from, next) => {
            if (!store.getters["modules/currentLesson"]) {
              next({ name: "modules" });
            } else next();
          },
          redirect: {
            name: "introduction",
          },
          children: [
            {
              path: "teacher-lesson",
              name: "teacher-lesson",
              component: loadComponent("Lessons/Lesson/TeacherLesson"),
              redirect: { name: "introduction" },
              children: [
                {
                  path: "introduction",
                  name: "introduction",
                  component: loadComponent("Lessons/Lesson/TeacherLesson/Introduction"),
                },
                {
                  path: "guided-lesson",
                  name: "guided-lesson",
                  component: loadComponent("Lessons/Lesson/TeacherLesson/TourLesson"),
                },
              ],
            },
            {
              path: "practice",
              name: "practice",
              component: loadComponent("Lessons/Lesson/Practice"),
            },
            {
              path: "grammar",
              name: "grammar",
              redirect: { name: "exercise", params: { index: 1 } },
              component: loadComponent("Lessons/Lesson/Grammar"),
              children: [
                {
                  path: "exercise/:index",
                  name: "exercise",
                },
              ],
            },
            {
              path: "contactUs",
              name: "contactUs",
              component: loadComponent("ContactUs"),
            },
            {
              path: "restore",
              name: "restore",
              component: loadComponent("Restore"),
              beforeEnter: (to, from, next) => {
                if (store.getters.profile?.role !== "TEACHER") {
                  next({ name: "modules" });
                } else next();
              },
            },
            {
              path: "progress",
              name: "progress",
              component: loadComponent("Progress"),
            },
          ],
        },
      ],
    },
    {
      path: "/admin",
      name: "admin",
      component: loadComponent("Admin"),
      redirect: { name: "admin-level" },
      beforeEnter: (to, from, next) => {
        if (!["ADMIN", "EDITOR", "ASSISTANT"].includes(store.getters.profile?.role)) {
          next({ name: "modules" });
        } else next();
      },
      meta: {
        requiresAuth: true,
        assistantCan: true,
      },
      children: [
        {
          path: "levels",
          name: "admin-level",
          component: loadComponent("Admin/Levels"),
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "modules",
          name: "admin-modules",
          component: loadComponent("Admin/Modules"),
        },
        {
          path: "lessons",
          name: "admin-lessons",
          component: loadComponent("Admin/Lessons"),
          meta: {
            editorCan: true,
          },
        },
        {
          path: "students",
          name: "admin-students",
          component: loadComponent("Admin/Students"),
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "teachers",
          name: "admin-teachers",
          component: loadComponent("Admin/Teachers"),
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "potential-users",
          name: "admin-potential-users",
          component: loadComponent("Admin/PotentialUsers"),
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "crm-all-contacts",
          name: "admin-all-contacts",
          component: loadComponent("Admin/CrmContacts"),
          props: {
            status: undefined,
          },
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "crm-active-contacts",
          name: "admin-active-contacts",
          component: loadComponent("Admin/CrmContacts"),
          props: {
            status: "ACTIVE",
          },
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "crm-inactive-contacts",
          name: "admin-inactive-contacts",
          component: loadComponent("Admin/CrmContacts"),
          props: {
            status: "INACTIVE",
          },
          meta: {
            assistantCan: true,
          },
        },
        {
          path: "crm-potential-contacts",
          name: "admin-potential-contacts",
          component: loadComponent("Admin/CrmContacts"),
          props: {
            status: "POTENTIAL",
          },
          meta: {
            assistantCan: true,
          },
        },
      ],
    },
    { path: "*", redirect: { name: "modules" } },
  ],
});
